import {
  buildAutofillRowData,
  parseBoolean,
  type SchemaField,
} from "@/lib/tableBlockHelpers";

export type QuickAddPreviewField = {
  key: string;
  name: string;
  value: unknown;
  source: "captured" | "autofill";
};

export type QuickAddParseResult =
  | { status: "missing-regex" }
  | { status: "invalid-regex"; error: string }
  | { status: "empty-text" }
  | { status: "no-match" }
  | {
      status: "ok";
      fields: QuickAddPreviewField[];
      rowData: Record<string, unknown>;
    };

function coerceCapturedValue({
  field,
  raw,
}: {
  field: SchemaField;
  raw: string;
}): { value?: unknown } {
  if (field.type === "date") {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return { value: parsed.toISOString().split("T")[0] };
    }
    return { value: trimmed.slice(0, 10) };
  }
  if (field.type === "number") {
    const num = Number(raw);
    return { value: Number.isNaN(num) ? undefined : num };
  }
  if (field.type === "checkbox") {
    return { value: parseBoolean(raw) };
  }
  return { value: raw };
}

function resolveSchemaFieldForCapture({
  captureName,
  byKey,
}: {
  captureName: string;
  byKey: Map<string, SchemaField>;
}): SchemaField | undefined {
  const key = captureName.startsWith("$") ? captureName.slice(1) : captureName;
  const exact = byKey.get(key);
  if (exact) return exact;
  if (!key.includes("_")) return undefined;
  return byKey.get(key.replaceAll("_", " "));
}

export function parseQuickAddText({
  text,
  extractRegex,
  schema,
}: {
  text: string;
  extractRegex: string | undefined;
  schema: SchemaField[];
}): QuickAddParseResult {
  const patternSource = extractRegex?.trim();
  if (!patternSource) {
    return { status: "missing-regex" };
  }
  if (text === "") {
    return { status: "empty-text" };
  }

  let pattern: RegExp;
  try {
    pattern = new RegExp(patternSource);
  } catch (e) {
    const error = e instanceof Error ? e.message : "Invalid regular expression";
    return { status: "invalid-regex", error };
  }

  const match = text.match(pattern);
  const groups = match?.groups;
  if (!groups) {
    return { status: "no-match" };
  }

  const byKey = new Map(schema.map((field) => [field.key, field]));
  const captured: Record<string, { value?: unknown }> = {};
  for (const [captureName, raw] of Object.entries(groups)) {
    const field = resolveSchemaFieldForCapture({ captureName, byKey });
    if (!field) continue;
    captured[field.key] = coerceCapturedValue({ field, raw: raw ?? "" });
  }

  if (Object.keys(captured).length === 0) {
    return { status: "no-match" };
  }

  const autofill = buildAutofillRowData({ schema }) as Record<
    string,
    { value?: unknown }
  >;
  const rowData: Record<string, unknown> = { ...autofill, ...captured };

  const fields: QuickAddPreviewField[] = [];
  for (const field of schema) {
    if (field.key in captured) {
      fields.push({
        key: field.key,
        name: field.name,
        value: captured[field.key]?.value,
        source: "captured",
      });
      continue;
    }
    if (field.key in autofill) {
      fields.push({
        key: field.key,
        name: field.name,
        value: autofill[field.key]?.value,
        source: "autofill",
      });
    }
  }

  return { status: "ok", fields, rowData };
}
